import createFetchClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./generated";

export class HttpError extends Error {
  readonly status: number;

  constructor(url: string, status: number, statusText: string) {
    super(`${url}: ${status} ${statusText}`);
    this.name = "HttpError";
    this.status = status;
  }
}

export function flattenQueryParams(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();

  function addParam(key: string, value: unknown) {
    if (value === null || value === undefined) return;

    if (typeof value === "object" && !Array.isArray(value)) {
      const obj = value as Record<string, unknown>;
      for (const [nestedKey, nestedValue] of Object.entries(obj)) {
        if (nestedValue !== null && nestedValue !== undefined) {
          searchParams.append(nestedKey, String(nestedValue));
        }
      }
    } else if (Array.isArray(value)) {
      for (const item of value) {
        searchParams.append(key, String(item));
      }
    } else {
      searchParams.append(key, String(value));
    }
  }

  for (const [key, value] of Object.entries(params)) {
    addParam(key, value);
  }

  return searchParams.toString();
}

export interface ErrorContext {
  url?: string;
  status?: number;
  baseUrl?: string;
}

export interface ApiClientConfig {
  baseUrl: string;
  middleware?: Middleware[];
  querySerializer?: (params: Record<string, unknown>) => string;
  onError?: (error: unknown, context: ErrorContext) => void;
  fetch?: typeof globalThis.fetch;
}

export function createApiClient(config: ApiClientConfig) {
  const fetchClient = createFetchClient<paths, "*/*">({
    baseUrl: config.baseUrl,
    ...(config.querySerializer && { querySerializer: config.querySerializer }),
    ...(config.fetch && { fetch: config.fetch }),
  });

  const errorMiddleware: Middleware = {
    onResponse: async ({ response }) => {
      if (!response.ok) {
        const error = new HttpError(
          response.url,
          response.status,
          response.statusText,
        );
        config.onError?.(error, {
          url: response.url,
          status: response.status,
        });
        throw error;
      }
      return response;
    },

    onError({ error, request }) {
      if (error instanceof Error && error.name === "AbortError") {
        return error;
      }

      const requestUrl = request?.url ?? "(no request url)";
      config.onError?.(error, { url: requestUrl, baseUrl: config.baseUrl });
      return new Error(
        `API Error: baseUrl=${config.baseUrl} requestUrl=${requestUrl}`,
        { cause: error },
      );
    },
  };

  for (const mw of config.middleware ?? []) {
    fetchClient.use(mw);
  }
  fetchClient.use(errorMiddleware);

  return fetchClient;
}
